import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSubmitProfile } from '@/hooks/useProfile';
import { useAuthStore } from '@/store/authStore';
import { markProfileSkippedLocally } from '@/lib/onboarding-profile';
import { cn } from '@/lib/utils';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'];
const INDUSTRIES = [
  'Software / SaaS',
  'Financial Services',
  'Healthcare',
  'Retail / E-commerce',
  'Government',
  'Media',
  'Other',
];
const USE_CASES = [
  'Engineering',
  'Security / Compliance',
  'Data / ML',
  'Product',
  'Founder / Exec',
  'Other',
];
const STACK_OPTIONS = [
  'AWS',
  'Azure',
  'GCP',
  'Okta',
  'Entra',
  'pgvector',
  'Pinecone',
  'Weaviate',
  'Qdrant',
  'Other',
];

interface ProfileFormProps {
  onDone: () => void;
}

export function ProfileForm({ onDone }: ProfileFormProps) {
  const currentOrgName = useAuthStore((s) => s.user?.organization?.name) ?? '';
  const [orgName, setOrgName] = useState(currentOrgName);
  const [companySize, setCompanySize] = useState<string>();
  const [industry, setIndustry] = useState<string>();
  const [useCase, setUseCase] = useState<string>();
  const [stack, setStack] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);

  const submit = useSubmitProfile();

  const toggleStack = (tag: string) => {
    setStack((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit.mutate(
      {
        organization_name: orgName.trim() || undefined,
        company_size: companySize,
        industry,
        use_case: useCase,
        primary_stack: stack.length ? stack : undefined,
        marketing_consent: consent,
      },
      { onSuccess: onDone },
    );
  };

  const handleSkip = () => {
    markProfileSkippedLocally();
    submit.mutate({ skipped: true }, { onSuccess: onDone, onError: onDone });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="org-name">Company / organization name</Label>
        <Input
          id="org-name"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          placeholder="Acme Inc"
          maxLength={100}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Company size</Label>
          <Select value={companySize} onValueChange={setCompanySize}>
            <SelectTrigger>
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Industry</Label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Your role / use case</Label>
        <Select value={useCase} onValueChange={setUseCase}>
          <SelectTrigger>
            <SelectValue placeholder="What best describes you?" />
          </SelectTrigger>
          <SelectContent>
            {USE_CASES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Primary stack</Label>
        <div className="flex flex-wrap gap-2">
          {STACK_OPTIONS.map((tag) => {
            const active = stack.includes(tag);
            return (
              <button
                type="button"
                key={tag}
                onClick={() => toggleStack(tag)}
                aria-pressed={active}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent',
                )}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
        />
        <span>I agree to receive product updates and onboarding tips from Gateco.</span>
      </label>

      <div className="flex items-center justify-between pt-2">
        <Button type="button" variant="ghost" onClick={handleSkip} disabled={submit.isPending}>
          Skip for now
        </Button>
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Continue
        </Button>
      </div>
    </form>
  );
}
